import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLanguage } from "../../Translations/LenguageContext";
import ErrorPage from "../../Components/ErrorPage";
import EmptyView from "../ViewComponents/EmptyView";
import DeleteRecordModal from "../ViewComponents/DeleteRecordModal";
import { Dropdown } from "react-bootstrap";
import { IoOpenOutline } from "react-icons/io5";
import LoadingCubes from "../ViewComponents/LoadingCubes";
import { TbGalaxy } from "react-icons/tb";
import { DiMsqlServer } from "react-icons/di";
import { BiLogoPostgresql } from "react-icons/bi";
import { GrMysql } from "react-icons/gr";

const MainDatabase = ({ userData, no_session }) => {
    const { t } = useLanguage()
    const navigate = useNavigate();
    const { project_id } = useParams()
    const user_id = sessionStorage.getItem("user_id");
    const [records, setRecords] = useState([]);
    const [filteredRecords, setFilteredRecords] = useState([]);
    const [activeFilter, setActiveFilter] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedRecordId, setSelectedRecordId] = useState(null);
    const [animateReset, setAnimateReset] = useState(false)

    const new_item_navigate = `/project_panel/${project_id}/databases/new_database_nosql`
    const title_empty = "General purpose databases"
    const subtitle_empty = "Each database can contain multiple encrypted collections of data."
    const item = "nosql"


    const navigateToNewProject = () => {
        navigate(new_item_navigate);
    };

    const fetchRecords = async () => {
        setLoading(true)
        try {
            const response = await fetch("https://backend.erioon.com/get_dbs", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ user_id }),
            });
            if (response.ok) {
                const result = await response.json();
                let accessData = result.databases || {};
                let filteredData = [];

                for (let key in accessData) {
                    if (accessData[key].project_id === project_id) {
                        filteredData.push(accessData[key]);
                    }
                }

                const recordsArray = Object.values(filteredData);
                recordsArray.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                setRecords(recordsArray);
                setFilteredRecords(recordsArray);
                setActiveFilter("newest")
            } else {
                setError(true);
            }
        } catch (err) {
            setLoading(false)
            setError(true);
        } finally {
            setTimeout(() => {
                setLoading(false)
            }, 500);
        }
    };

    useEffect(() => {
        if (user_id) {
            fetchRecords();
        } else {
            setError(true);
            setLoading(false);
        }
    }, [user_id]);

    const handleSort = (criteria) => {
        setActiveFilter(criteria);
        let sortedRecords = [...records];
        if (criteria === "alphabetical") {
            sortedRecords.sort((a, b) => a.db_name.localeCompare(b.db_name));
        } else if (criteria === "reverseAlphabetical") {
            sortedRecords.sort((a, b) => b.db_name.localeCompare(a.db_name));
        } else if (criteria === "newest") {
            sortedRecords.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        } else if (criteria === "oldest") {
            sortedRecords.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        }
        setFilteredRecords(sortedRecords);
    };

    const resetFilters = () => {
        setActiveFilter("");
        setSearchQuery("")
        setAnimateReset(true)
        setTimeout(() => {
            setAnimateReset(false)
        }, 1000);
        setFilteredRecords(records);
    };

    const handleSearch = (event) => {
        const query = event.target.value.toLowerCase();
        setSearchQuery(query);

        if (query.trim() === "") {
            setFilteredRecords(records);
            return;
        }

        const filtered = records.filter((record) => {
            return (
                record._id.toLowerCase().includes(query) ||
                record.project_id.toLowerCase().includes(query) ||
                record.db_name.toLowerCase().includes(query) ||
                record.db_desc.toLowerCase().includes(query) ||
                record.plan.toLowerCase().includes(query) ||
                record.provider.toLowerCase().includes(query) ||
                record.timestamp.toLowerCase().includes(query)

            );
        });

        setFilteredRecords(filtered);
    };

    const handleDeleteClick = (accID) => {
        setSelectedRecordId(accID);
        setShowDeleteModal(true);
    };

    const handleCloseModal = () => {
        setShowDeleteModal(false);
        setSelectedRecordId(null);
    };

    const handleEditClick = (accID) => {
        const recordObject = records.find((record) => record._id === accID)
        sessionStorage.setItem("access_object", JSON.stringify(recordObject))
        navigate(`/project_panel/${project_id}/access/update_access/${accID}`)
    };

    const regionDisplayNames = {
        "eastus": "East-US",
        "eastus2": "East-US 2",
        "westus": "West-US",
        "westus3": "West-US 3",
        "centralus": "Central-US",
        "northcentralus": "North Central-US",
        "southcentralus": "South Central-US",
        "westcentralus": "West Central-US",
        "canadacentral": "Canada Central",
        "canadaeast": "Canada East",
        "northeurope": "North Europe",
        "westeurope": "West Europe",
        "francecentral": "France Central",
        "germanywestcentral": "Germany West Central",
        "norwayeast": "Norway East",
        "swedencentral": "Sweden Central",
        "switzerlandnorth": "Switzerland North",
        "ukwest": "UK West",
        "uksouth": "UK South",
        "italynorth": "Italy North",
        "spaincentral": "Spain Central",
        "polandcentral": "Poland Central",
        "centralindia": "Central India",
        "southindia": "South India",
        "westindia": "West India",
        "japaneast": "Japan East",
        "japanwest": "Japan West",
        "koreacentral": "Korea Central",
        "koreasouth": "Korea South",
        "southeastasia": "Southeast Asia",
        "eastasia": "East Asia",
        "newzealandnorth": "New Zealand North",
        "brazilsouth": "Brazil South",
        "brazilsoutheast": "Brazil Southeast",
        "southafricanorth": "South Africa North",
        "uaenorth": "UAE North",
        "qatarcentral": "Qatar Central",
        "australiaeast": "Australia East",
        "australiacentral": "Australia Central",
        "australiasoutheast": "Australia Southeast"
    };


    if (loading) {
        return <LoadingCubes />;
    } else if (error) {
        return <ErrorPage />;
    } else if (records.length === 0) {
        return <EmptyView navigate_link={new_item_navigate} title={title_empty} subtitle={subtitle_empty} item={item} />
    }

    return (
        <div className="main_view_container">
            <div className="header_main_view fade-in">
                <div className="header_title">
                    <p className="main_view_title">Database</p>
                    <p className="main_view_subtitle">Each database can contain multiple encrypted collections of data.</p>
                    <button className="connect_email_btn" onClick={navigateToNewProject}>
                        <i className="fa-solid fa-plus"></i> {t.create}
                    </button>
                </div>
                {/* <img className="icon_img" src={item} alt="Access icon" /> */}
            </div>
            <div className="filter_container fade-in">
                {records.length > 1 && (
                    <>
                        <button
                            className={`filter_btn ${activeFilter === "alphabetical" ? "clicked_filter" : ""}`}
                            onClick={() => handleSort("alphabetical")}
                        >
                            <i className="fa-solid fa-arrow-down-wide-short"></i> A-Z
                        </button>
                        <button
                            className={`filter_btn ${activeFilter === "reverseAlphabetical" ? "clicked_filter" : ""}`}
                            onClick={() => handleSort("reverseAlphabetical")}
                        >
                            <i className="fa-solid fa-arrow-up-short-wide"></i> Z-A
                        </button>
                        <button
                            className={`filter_btn ${activeFilter === "newest" ? "clicked_filter" : ""}`}
                            onClick={() => handleSort("newest")}
                        >
                            {t.from_newest}
                        </button>
                        <button
                            className={`filter_btn ${activeFilter === "oldest" ? "clicked_filter" : ""}`}
                            onClick={() => handleSort("oldest")}
                        >
                            {t.from_oldest}
                        </button>
                    </>
                )}
                <button
                    className={`filter_btn ${activeFilter === "reset" ? "clicked_filter" : ""}`}
                    onClick={resetFilters}
                >
                    <i className={`fa-solid fa-arrows-rotate ${animateReset ? "activated_animation" : ""}`}></i>
                </button>
                <div className="filter_input_cont">
                    <i className="fa-solid fa-magnifying-glass"></i>
                    <input
                        className="filter_input"
                        placeholder={t.search}
                        value={searchQuery}
                        onChange={handleSearch}
                    />
                </div>
            </div>
            <div className="organizations_cont">
                {filteredRecords.map((record) => (
                    <div key={record._id} className="organization_item fade-in">
                        <p className="organization_title" onClick={() => navigate(`/project_panel/${project_id}/databases/${record._id}/data`)}><span>{record.db_name}</span> <i><IoOpenOutline /></i></p>
                        <p className="organization_desc">{record.db_desc}</p>


                        <div className="organization_footer">
                            <Dropdown>
                                <div className="property_btn_action_toggle">
                                    <Dropdown.Toggle className="custom-dropdown-toggle" as="div">
                                        <i className="fa-solid fa-ellipsis-vertical"></i>
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu className="custom-dropdown-menu">
                                        {/* <Dropdown.Item
                                            className="drop_down_item"
                                            onClick={() => handleEditClick(record._id)}
                                        >
                                            <i className="fa-solid fa-pencil"></i> {t.edit} {record.project_name}
                                        </Dropdown.Item> */}
                                        <Dropdown.Item
                                            className="drop_down_item delete"
                                            onClick={() => handleDeleteClick(record._id)}
                                        >
                                            <i className="fa-solid fa-trash"></i> {t.delete}
                                        </Dropdown.Item>

                                    </Dropdown.Menu>
                                </div>
                            </Dropdown>
                            <div className="footer_items_org">
                                <div className="footer_items_org_items">

                                    <p className="selected_region_footer">{regionDisplayNames[record.selectedRegion]}</p>

                                    <p>{record.db_type == "nosql" ? (
                                        <i><TbGalaxy /></i>
                                    ) : (
                                        <>
                                            {record.sql_type == "postgresql" ? (
                                                <i><BiLogoPostgresql /></i>
                                            ) :record.sql_type == "sqlserver" ? (
                                                <i><DiMsqlServer /></i>
                                            ): (
                                                <i><GrMysql /></i>
                                            )}
                                        </>
                                    )}</p>
                                </div>

                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <DeleteRecordModal
                show={showDeleteModal}
                handleClose={handleCloseModal}
                recordObject={records.find((record) => record._id === selectedRecordId)}
                fetchRecords={fetchRecords}
                endpoint={"delete_database"}
                record_type={"database"}
                message="This action can be undone later. This database will be deleted but data will be preserved in backup."
            />
        </div>
    );
};

export default MainDatabase;
